'use strict';

import {isArray} from './Common.js';

// Point Class
const Point2D = (function(){
  const c = function(v, y){
    const argLen = arguments.length;
    if (argLen == 1) {
      if (v) {
        if (isArray(v)) {
          c.apply(this, v);
          return;
        } else if (typeof(v) == 'object') {
          this.x = v.x || v.left || v.width || 0;
          this.y = v.y || v.top || v.height || 0;
          return;
        }
      }
    }
    this.x = v || 0;
    this.y = y || 0;
  };
  c.prototype.set = function(v, y){
    const argLen = arguments.length;
    if (argLen == 1) {
      if (v) {
        if (isArray(v)) {
          c.apply(this, v);
          return;
        } else if (typeof(v) == 'object') {
          this.x = v.x || v.left || v.width || 0;
          this.y = v.y || v.top || v.height || 0;
          return;
        }
      }
    }
    this.x = v || 0;
    this.y = y || 0;
  };
  c.prototype.copy = function(p) {
    this.x = p.x;
    this.y = p.y;
  }
  c.prototype.toString = function(){
    return ['[', this.x, ',', this.y, ']'].join('');
  };
  c.prototype.add = function(v, y){
    const argLen = arguments.length;
    const ret = new c(this);
    if (argLen == 1) {
      if (typeof(v) == 'object') {
        ret.x += v.x || 0;
        ret.y += v.y || 0;
      } else {
        ret.x += v;
        ret.y += v;
      }
      return ret;
    }
    ret.x += v;
    ret.y += y;
    return ret;
  };
  c.prototype.sub = function(v, y){
    const argLen = arguments.length;
    const ret = new c(this);
    if (argLen == 1) {
      if (typeof(v) == 'object') {
        ret.x -= v.x || 0;
        ret.y -= v.y || 0;
      } else {
        ret.x -= v;
        ret.y -= v;
      }
      return ret;
    }
    ret.x -= v;
    ret.y -= y;
    return ret;
  };
  c.prototype.mul = function(v){
    const ret = new c(this);
    ret.x *= v;
    ret.y *= v;
    return ret;
  };
  c.prototype.div = function(v){
    const ret = new c(this);
    ret.x /= v;
    ret.y /= v;
    return ret;
  };
  c.prototype.abs = function(v){
    const ret = new c(this);
    ret.x = Math.abs(ret.x);
    ret.y = Math.abs(ret.y);
    return ret;
  };
  c.prototype.min = function(v){
    return new c(Math.min(this.x, v.x), Math.min(this.y, v.y));
  };
  c.prototype.max = function(v){
    return new c(Math.max(this.x, v.x), Math.max(this.y, v.y));
  };
  c.prototype.floor = function(){
    return new c(Math.floor(this.x), Math.floor(this.y));
  };
  c.prototype.ceil = function(){
    return new c(Math.ceil(this.x), Math.ceil(this.y));
  };
  c.prototype.round = function(){
    return new c(Math.round(this.x), Math.round(this.y));
  };
  c.prototype.negate = function(){
    return new c(-this.x, -this.y);
  };
  c.prototype.distance = function(){
    return Math.sqrt(this.x * this.x + this.y * this.y);
  };
  c.prototype.angle = function(){
    return Math.atan2(this.y, this.x);
  };
  c.prototype.equals = function(){
    const p = new c(arguments);
    return this.x == p.x && this.y == p.y;
  };
  c.prototype.rotate = function(a){
    const cr = Math.cos(a);
    const sr = Math.sin(a);
    return new c(
      cr * this.x + sr * this.y,
      -sr * this.x + cr * this.y
    );
  };
  c.prototype.applyTransform = function(m) {
    const p = [this.x, this.y];
    const xt = p[0] * m[0] + p[1] * m[2] + m[4];
    const yt = p[0] * m[1] + p[1] * m[3] + m[5];
    return new c(xt, yt);
  };
  c.prototype.applyInverseTransform = function(m) {
    const p = [this.x, this.y];
    const d = m[0] * m[3] - m[1] * m[2];
    const xt = (p[0] * m[3] - p[1] * m[2] + m[2] * m[5] - m[4] * m[3]) / d;
    const yt = (-p[0] * m[1] + p[1] * m[0] + m[4] * m[1] - m[5] * m[0]) / d;
    return new c(xt, yt);
  }
  c.prototype.clone = function(){
    return new c(this);
  };
  return c;
})();

const Rect = (function(){
  const c = function(){
    const args = arguments;
    const argLen = arguments.length;
    if (argLen >= 4) {
      // x, y, w, h;
      this.position = new Point2D(args[0], args[1]);
      this.size = new Point2D(args[2], args[3]);
    } else if (argLen == 3) {
      if (typeof(args[0]) == 'object') {
        // (x,y), w, h
        this.position = new Point2D(args[0]);
        this.size = new Point2D(args[1], args[2]);
      } else {
        // x, y, (w, h)
        this.position = new Point2D(args[0], args[1]);
        this.size = new Point2D(args[2]);
      }
    } else if (argLen == 2) {
      // (x,y), (w,h)
      this.position = new Point2D(args[0]);
      this.size = new Point2D(args[1]);
    } else if (argLen == 1) {
      if (args[0].position) {
        // (x,y,w,h)
        this.position = new Point2D(args[0].position);
        this.size = new Point2D(args[0].size);
      } else {
        // [x,y,w,y]
        this.position = new Point2D(args[0][0], args[0][1]);
        this.size = new Point2D(args[0][2], args[0][3]);
      }
    } else {
      // (0,0,0,0)
      this.position = new Point2D();
      this.size = new Point2D();
    }
  };
  c.prototype.center = function(){
    return this.position.add(this.size.div(2));
  };
  return c;
})();

export {Point2D, Rect};
